/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { cloneDeep, startsWith } from 'lodash';
import { Field } from 'react-final-form';
import { StyleSheet, css } from 'aphrodite';
import { LinkButton, InputContainer, TextInput } from '@blend/enterprise-components';

import { login } from 'id-api/auth';
import { FormCard } from 'id-components/FormCard';
import { getClientAppConfig } from 'id-utils/clientAppConfig';
import { getConfig } from 'id-utils/config';
import { extractValue, useLoginFlowSubmit } from 'id-utils/login';
import { getParsedCookieValue, getCookieValue } from 'id-utils/cookies';
import { REMEMBER_MFA_DEVICE_COOKIE } from 'id-constants/global';
import { rememberMeInputConfig, usernameInputConfig, passwordInputConfig } from 'id-constants/commonInputs';

import { SigninHelpModal } from './SigninHelpModal/SigninHelpModal';
import { ForgotPasswordLinkButton } from '../ForgotPasswordPage/ForgotPasswordLinkButton';
import { ConfigContext } from 'id-components/ConfigContext/ConfigContext';

export const styles = StyleSheet.create({
  linkBarContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  spacer: {
    ':not(:last-child)': {
      marginBottom: 16,
    },
  },
});

export const SignInPage: React.FC = () => {
  const config = useContext(ConfigContext);
  const [isSigninHelpModalOpen, setSigninHelpModalOpen] = useState<boolean>(false);
  const [clientAppConfig, setClientAppConfig] = useState<Identity.API.ClientAppConfig>();

  useEffect(() => {
    void (async (): Promise<void> => {
      const { clientAppIdCookieName } = getConfig();
      const clientAppId = getCookieValue(clientAppIdCookieName) || '';
      if (clientAppId) {
        setClientAppConfig(await getClientAppConfig(clientAppId));
      }
    })();
  }, []);

  const { username: usernameFromParams, password: passwordFromParams } = (() => {
    const qs = window.location.search.slice(1);
    // some passwords also have & so we have to manually parse this
    const { unformattedUser, unformattedPassword } = (() => {
      if (!startsWith(qs, 'username')) {
        return { unformattedPassword: undefined, unformattedUser: undefined };
      }
      const firstArgEnd = qs.indexOf('&');
      if (firstArgEnd !== -1) {
        return { unformattedPassword: qs.slice(firstArgEnd + 1), unformattedUser: qs.slice(0, firstArgEnd) };
      } else {
        return { unformattedPassword: undefined, unformattedUser: qs };
      }
    })();

    const username = unformattedUser && extractValue(decodeURIComponent(unformattedUser));
    const password = unformattedPassword && extractValue(decodeURIComponent(unformattedPassword));
    return {
      password,
      username,
    };
  })();

  const onSubmit = useLoginFlowSubmit<Identity.API.LoginBody, Identity.API.LoginBody>(login, formValues => {
    const formattedValues = cloneDeep(formValues);
    return formattedValues;
  });
  return (
    <FormCard<Identity.API.LoginBody>
      additionalFooterComponent={
        !getParsedCookieValue(REMEMBER_MFA_DEVICE_COOKIE) && clientAppConfig?.mfaRequired ? (
          <Field {...rememberMeInputConfig} />
        ) : undefined
      }
      headerTitle="Welcome"
      id="it-SignIn"
      onSubmit={onSubmit}
      showCancel={false}
      submitLabel="Sign in"
      showSSOButton={!!config.auth?.saml?.tprSSO?.allowTPRSSO}
      showBlendSSOButton={!!config.allowBlendSSO}
    >
      <Field initialValue={usernameFromParams} {...usernameInputConfig} />
      {!passwordFromParams ? (
        <Field {...passwordInputConfig} />
      ) : (
        <div className={css(styles.spacer)}>
          <InputContainer
            customComponentProps={{ disabled: true, type: 'password', value: passwordFromParams }}
            input={{ name: 'password' }}
            inputComponent={TextInput}
            label="Password"
            labelSize="sm"
            meta={{}}
          />
        </div>
      )}
      <div className={css(styles.linkBarContainer)}>
        <ForgotPasswordLinkButton usernameFromParams={usernameFromParams} />
        <LinkButton
          id="it-SignInHelp"
          label="Having trouble signing in?"
          onClick={() => setSigninHelpModalOpen(true)}
        />
      </div>
      <SigninHelpModal handleClose={() => setSigninHelpModalOpen(false)} isOpen={isSigninHelpModalOpen} />
    </FormCard>
  );
};
